import { useState } from 'react';
import { apiRepository } from '../../../api/apiRepository';
import { ValidationError } from '../../form/validationForm';
import { findProductInCart } from '../../utils/couponUtils';
import { UseCouponValidationOptions } from '../../Interface/hooksInterface';
import { getCurrentLocalDate, convertStringToLocalDate } from '../../utils/dateUtils';

export const useCouponValidation = ({ cart, setCouponDiscount, generalCouponUsed, setGeneralCouponUsed, couponCode, productCouponUsed, setProductCouponUsed, categorieCouponUsed, setCategorieCouponUsed }: UseCouponValidationOptions) => {
  const [info, setInfo] = useState<ValidationError[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const validateCode = async () => {
    try {
      setLoading(true);
      setInfo([]);

      if(generalCouponUsed || productCouponUsed || categorieCouponUsed) {
        setInfo([{ field: 'error', message: "Solo se puede aplicar un cupón." }]);
        return;
      }

      const response = await apiRepository.validateCouponCode(couponCode);
      const couponData = response.data.data;
      const { typeGeneralProduct, typeDiscount, amount, code, packetId, packageTypeId, dateExpired } = couponData;

      // Validación de expiración
      if (convertStringToLocalDate(dateExpired) < getCurrentLocalDate()) {
        setInfo([{ field: 'error', message: "Este cupón ya ha expirado" }]);
        return;
      }

      // CUPÓN GENERAL (typeGeneralProduct === 0)
      if (typeGeneralProduct === 0) {
        if (generalCouponUsed) {
          setInfo([{ field: 'error', message: "Ya se ha aplicado un cupón general." }]);
          return;
        }

        const discount = typeDiscount
          ? amount / 100 // porcentaje
          : amount;      // monto fijo

        setCouponDiscount((prev) => ({
          ...prev,
          generalDiscount: {
            couponName: code,
            couponDiscount: discount,
            type: typeDiscount ? "percentage" : "fixed",
          },
        }));

        setGeneralCouponUsed(true);
        setInfo([{ field: 'success', message: 'Cupón general aplicado correctamente.' }]);
        return;
      }

      // CUPÓN POR PRODUCTO (typeGeneralProduct === 1)
      if (typeGeneralProduct === 1) {
        if (!packetId) {
          setInfo([{ field: 'error', message: "Este cupón no tiene un producto asociado." }]);
          return;
        }

        if (productCouponUsed) {
          setInfo([{ field: 'error', message: "Ya se aplicó un cupón a este producto." }]);
          return;
        }

        const productInCart = findProductInCart(cart, packetId);

        if (!productInCart) {
          setInfo([{ field: 'error', message: 'El producto no está en el carrito.' }]);
          return;
        }

        const discount = typeDiscount
          ? amount
          : amount;

        if (discount > productInCart.price * productInCart.quantity) {
          setInfo([{ field: 'error', message: 'El descuento es mayor al total del producto.' }]);
          return;
        }

        setCouponDiscount((prev) => ({
          ...prev,
          specificDiscount: [
            ...(prev.specificDiscount || []),
            {
              couponName: couponData.packetName,
              couponDiscount: discount,
              type: typeDiscount ? "percentage" : "fixed",
              packageId: packetId,
              packetName: couponData.packetName,
              couponCode: code,
            },
          ],
        }));

        setProductCouponUsed(true);
        setInfo([{ field: 'success', message: `Cupón aplicado a ${couponData.packetName || "producto seleccionado"}.` }]);
        return;
      }

      // CUPÓN POR CATEGORÍA (typeGeneralProduct === 2)
      if (typeGeneralProduct === 2) {
        const matchingProducts = cart.filter((product: any) => product.packageTypeId === packageTypeId);

        if (matchingProducts.length === 0) {
          setInfo([{ field: 'error', message: 'Ningún producto coincide con la categoría del cupón.' }]);
          return;
        }

        if (categorieCouponUsed) {
          setInfo([{ field: 'error', message: 'Ya se aplicó un cupón a esta categoría.' }]);
          return;
        }

        const discounts = matchingProducts.map((product: any) => {
          const discount = typeDiscount
            ? amount
            : amount;

          return {
            couponName: couponData.packetName || code,
            couponDiscount: discount,
            type: typeDiscount ? "percentage" : "fixed",
            packageId: product.id,
            packetName: product.name,
            couponCode: code,
          };
        });

        setCouponDiscount((prev) => ({
          ...prev,
          specificDiscount: [
            ...(prev.specificDiscount || []),
            ...discounts,
          ],
        }));

        setCategorieCouponUsed(true);
        setInfo([{ field: 'success', message: `Cupón aplicado a categoría con ${matchingProducts.length} productos.` }]);
        return;
      }

      // Tipo de cupón no reconocido
      setInfo([{ field: 'error', message: 'Tipo de cupón no reconocido.' }]);

    } catch (error) {
      const errorMessage =
        error?.response?.data?.message?.replace(/^Internal server error: /, '') ||
        "Ocurrió un error al intentar validar el cupón. Inténtalo de nuevo.";
      setInfo([{ field: 'error', message: errorMessage }]);
    } finally {
      setLoading(false);
    }
  }

  const removeCoupon = (itemId: string) => {
    setInfo([]);
    setCouponDiscount((prev) => {
      const specificDiscount = prev.specificDiscount?.filter((discount) => discount.packageId !== itemId) || [];
      const generalDiscount = itemId === "general" ? null : prev.generalDiscount;

      return {
        ...prev,
        specificDiscount,
        generalDiscount,
      };
    });

    if (itemId === "general") {
      setGeneralCouponUsed(false);
    }

    setProductCouponUsed(false);
    setCategorieCouponUsed(false);

    setInfo([{ field: 'success', message: "Cupón retirado con éxito." }]);
  };

  return { validateCode, info, loading, removeCoupon, setInfo };
};